/**
 * Unit-system-aware length formatting (metric <-> imperial).
 *
 * The whole app stores and computes in metric (metres for building
 * dimensions, millimetres for detail). Engine BOM / cut-list strings keep
 * their metric millimetre labels as stable pricing IDs and are NOT converted.
 * These helpers run at the UI/PDF render boundary so the intl market can show
 * feet/inches while every calculation — and the se market — stays metric.
 */

export type UnitSystem = "metric" | "imperial";

const MM_PER_INCH = 25.4;
const INCHES_PER_FOOT = 12;
/** Imperial detail lengths are rounded to the nearest 1/16". */
const FRACTION_DENOMINATOR = 16;

function reduceFraction(
	numerator: number,
	denominator: number,
): [number, number] {
	let a = numerator;
	let b = denominator;
	while (b) {
		[a, b] = [b, a % b];
	}
	const divisor = a || 1;
	return [numerator / divisor, denominator / divisor];
}

/**
 * Format a whole number of inches as feet + inches, e.g. 98 → `8' 2"`.
 * Values under one foot render as inches only (`9"`).
 */
function formatFeetInches(totalInches: number): string {
	const rounded = Math.round(totalInches);
	const feet = Math.floor(rounded / INCHES_PER_FOOT);
	const inches = rounded - feet * INCHES_PER_FOOT;
	if (feet <= 0) return `${inches}"`;
	if (inches === 0) return `${feet}'`;
	return `${feet}' ${inches}"`;
}

/**
 * Format inches with a fractional part to the nearest 1/16", reducing the
 * fraction, e.g. 9.84" → `9 7/8"`. Handles carry when the fraction rounds to 1.
 */
function formatInchesFraction(totalInches: number): string {
	const whole = Math.floor(totalInches);
	let sixteenths = Math.round((totalInches - whole) * FRACTION_DENOMINATOR);
	let inches = whole;
	if (sixteenths === FRACTION_DENOMINATOR) {
		inches += 1;
		sixteenths = 0;
	}
	if (sixteenths === 0) return `${inches}"`;
	const [num, den] = reduceFraction(sixteenths, FRACTION_DENOMINATOR);
	if (inches === 0) return `${num}/${den}"`;
	return `${inches} ${num}/${den}"`;
}

const METRES_FORMATTERS: Record<string, Intl.NumberFormat> = {};
function metresFormatter(locale: string): Intl.NumberFormat {
	if (!METRES_FORMATTERS[locale]) {
		METRES_FORMATTERS[locale] = new Intl.NumberFormat(locale, {
			minimumFractionDigits: 1,
			maximumFractionDigits: 1,
		});
	}
	return METRES_FORMATTERS[locale];
}

const MM_FORMATTERS: Record<string, Intl.NumberFormat> = {};
function mmFormatter(locale: string): Intl.NumberFormat {
	if (!MM_FORMATTERS[locale]) {
		MM_FORMATTERS[locale] = new Intl.NumberFormat(locale, {
			maximumFractionDigits: 0,
		});
	}
	return MM_FORMATTERS[locale];
}

/**
 * Format a length given in METRES.
 * metric → `2.5 m`; imperial → `8' 2"` (feet + inches).
 */
export function formatLengthM(
	metres: number,
	unitSystem: UnitSystem,
	locale = "en-US",
): string {
	if (unitSystem === "imperial") {
		return formatFeetInches((metres * 1000) / MM_PER_INCH);
	}
	return `${metresFormatter(locale).format(metres)} m`;
}

/**
 * Format a length given in MILLIMETRES.
 * metric → `250 mm`; imperial → `9 7/8"` (inches to nearest 1/16").
 */
export function formatLengthMm(
	mm: number,
	unitSystem: UnitSystem,
	locale = "en-US",
): string {
	if (unitSystem === "imperial") {
		return formatInchesFraction(mm / MM_PER_INCH);
	}
	return `${mmFormatter(locale).format(Math.round(mm))} mm`;
}

/**
 * Format an area given in SQUARE METRES.
 * metric → `6.3 m²`; imperial → `67.8 sq ft`.
 */
export function formatAreaM2(
	squareMetres: number,
	unitSystem: UnitSystem,
	locale = "en-US",
): string {
	if (unitSystem === "imperial") {
		const sqft = squareMetres * 10.7639104;
		return `${new Intl.NumberFormat(locale, {
			maximumFractionDigits: 1,
		}).format(sqft)} sq ft`;
	}
	return `${new Intl.NumberFormat(locale, {
		maximumFractionDigits: 1,
	}).format(squareMetres)} m²`;
}
